/**
 * src/components/funnel/SiteFooter.jsx
 *
 * The footer, rebuilt for the funnel.
 *
 * The old footer was a four-column product directory — websites, funnels,
 * books, courses, the Vault, the podcast tiers. None of those are sold any
 * more, so it lists the funnel, the free resources the Tier C routing points
 * at, and the legal pages, and nothing else.
 *
 * Kept from the old footer: the mailing-list signup, which is still the only
 * way someone who is not ready to apply stays in touch. It posts through the
 * same Emailit-backed endpoint as before.
 */

import React from "react";
import { Link } from "react-router-dom";
import MailingListForm from "../landing/MailingListForm";
import { Wrap, Eyebrow } from "./primitives";

const COLUMNS = [
  {
    heading: "GrowthPoints",
    links: [
      { to: "/products", label: "All products & services" },
      { to: "/growth-points", label: "How it works" },
      { to: "/growth-points#plans", label: "Plans" },
      { to: "/growth-points/catalog", label: "Point catalog" },
      { to: "/apply", label: "Apply for a call" },
    ],
  },
  {
    heading: "Free resources",
    links: [
      { to: "/marketing-audit-free", label: "Marketing audit" },
      { to: "/seo-report", label: "SEO report" },
      { to: "/revenue-masterclass", label: "Revenue masterclass" },
      { to: "/blog", label: "Journal" },
    ],
  },
  {
    heading: "Agency",
    links: [
      { to: "/results", label: "Results" },
      { to: "/about-bow-tie-kreative", label: "About" },
      { to: "/case-studies", label: "Case studies" },
      { to: "/support", label: "Support" },
    ],
  },
  {
    heading: "Legal",
    links: [
      { to: "/terms-and-conditions", label: "Terms" },
      { to: "/privacy-policy", label: "Privacy" },
      { to: "/cookie-policy", label: "Cookies" },
      { to: "/refund-policy", label: "Refunds" },
    ],
  },
];

export default function SiteFooter({ mailingList = true }) {
  return (
    <footer
      data-testid="site-footer"
      className="border-t border-[color:var(--hairline-soft)] bg-ink-900"
    >
      {mailingList && (
        <div className="border-b border-[color:var(--hairline-soft)] py-16 md:py-20">
          <Wrap className="max-w-3xl">
            <Eyebrow>Not ready to apply</Eyebrow>
            <h2 className="rp-h3 mt-4 text-[color:var(--rp-heading)]">
              Get the thinking behind the work.
            </h2>
            <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-[color:var(--rp-body)]">
              Growth insights, worked examples and the occasional case study. Unsubscribe any
              time — we only email people who asked us to.
            </p>
            <div className="mt-8">
              <MailingListForm
                source="footer_signup"
                variant="footer"
                testidPrefix="mailing-list-footer-form"
              />
            </div>
          </Wrap>
        </div>
      )}

      <Wrap className="py-14 md:py-16">
        <div className="grid gap-10 md:grid-cols-[1.4fr_repeat(4,1fr)]">
          <div>
            <span className="flex items-center gap-3">
              <img src="/brand/btk-seal-white-64.webp" alt="" width={32} height={32} className="shrink-0" />
              <span className="text-[20px] font-bold uppercase leading-none tracking-[0.02em]">
                <span className="text-[color:var(--rp-heading)]">Bow Tie </span>
                <span className="text-brand">Kreative</span>
              </span>
            </span>
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-[color:var(--rp-body)]">
              The adaptive growth agency. One flexible point-based system for strategy, creative,
              funnels, content, automation and AI.
            </p>
            <p className="rp-eyebrow mt-5">Calgary, Alberta · Est. 2013</p>
          </div>

          {COLUMNS.map((column) => (
            <div key={column.heading}>
              <p className="rp-eyebrow">{column.heading}</p>
              <ul className="mt-4 space-y-2.5 list-none p-0">
                {column.links.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.to}
                      className="text-sm text-[color:var(--rp-body)] transition-colors hover:text-[color:var(--rp-heading)]"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-12 flex flex-col gap-3 border-t border-[color:var(--hairline-soft)] pt-8 text-[13px] text-[color:var(--rp-muted)] md:flex-row md:items-center md:justify-between">
          <p className="m-0">
            © {new Date().getFullYear()} Bow Tie Kreative. All rights reserved.
          </p>
          <p className="m-0">
            Published point values are estimates and vary with scope, urgency and complexity.
          </p>
          <a
            href="/sitemap.xml"
            className="text-[13px] text-[color:var(--rp-muted)] transition-colors hover:text-[color:var(--rp-heading)]"
          >
            Sitemap
          </a>
          {/* Rule attr.footer (block): the studio attribution ships in the
              footer legal row. Named here as the design system rather than
              the agency, because on Bow Tie's own site "built by Bow Tie
              Kreative" says nothing a reader does not already know. */}
          <p className="m-0">
            Built on the{" "}
            <a
              href="https://designsystem.bowtiekreative.com"
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-4 hover:text-[color:var(--rp-heading)]"
            >
              LAKA design system
            </a>
            .
          </p>
        </div>
      </Wrap>
    </footer>
  );
}
